import {
  AppBar,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  MenuList,
  Popper,
  Paper,
  SvgIcon,
  Tab,
  TabProps,
  Tabs,
  Toolbar,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Description, InsertDriveFile, Person, LockOpen, Group, CloudUpload, MonetizationOn, FolderOpenOutlined, MonetizationOnRounded, PublicOutlined, Update } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import laqusImf from 'assets/laqus-imf-digital-xsmall.png';
import { FuncaoParticipante, GrupoAcesso, Modulos, Perfil } from 'common/genericTypes';
import { Button, SmSpace } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link, LinkProps, RouteComponentProps, withRouter } from 'react-router-dom';
import { RootState } from 'store';
import { logout, saveModulos } from 'store/modules/auth';
import _ from 'lodash';
import SvgInvestidor from './icons/investidor';
import './header.css';

export const HEADER_WIDTH = '100px';

function TabWithSubmenu({ tab, index, value }: { tab: any, index: number, value: string }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? `submenu-popper-${index}` : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  React.useEffect(() => {
    if (open) {
      const handleOutsideClick = (event: MouseEvent) => {
        if (
          anchorEl && 
          !anchorEl.contains(event.target as Node) && 
          !document.getElementById(id!)?.contains(event.target as Node)
        ) {
          setAnchorEl(null);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [open, anchorEl, id]);

  return (
    <div className="menu-wrapper">
      <StyledTab
        disableRipple
        style={{ fontSize: '10px' }}
        icon={tab.icon}
        label={tab.label}
        value={tab.value}
        data-testid={typeof tab.label === 'string' ? `btnMenu${_.upperFirst(_.camelCase(tab.label))}` : undefined}
        aria-describedby={id}
        aria-owns={open ? id : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      />
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="right-start"
        className="submenu-popper"
        style={{ zIndex: 9999 }}
        modifiers={{
          offset: {
            enabled: true,
            offset: '0, 0'
          }
        }}
      >
        <Paper className="submenu">
          <MenuList>
            {tab.children.map((child: any, childIndex: number) => (
              <MenuItem
                key={childIndex}
                component={Link}
                to={child.to}
                selected={value === child.value}
                className="submenu-item"
                data-testid={typeof child.label === 'string' ? `btnMenu${_.upperFirst(_.camelCase(child.label))}` : undefined}
                onClick={() => setAnchorEl(null)}
              >
                {child.icon && <span className="submenu-icon">{child.icon}</span>}
                {child.label}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </div>
  );
}

const StyledTab = withStyles({
  root: {
    minWidth: '20px',
    maxWidth: HEADER_WIDTH,
  },
})(Tab);

const StyledIconButton = withStyles({
  root: {
    padding: '12px 0',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
})(IconButton);

function LinkTab<C extends React.ElementType>(props: TabProps<C, { component?: C }>) {
  return (
    <StyledTab
      {...props}
      disableRipple
      style={{ fontSize: '10px' }}
      data-testid={typeof props.label === 'string' ? `btnMenu${_.upperFirst(_.camelCase(props.label))}` : undefined}
    />
  );
}

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & RouteComponentProps;

const mapStateToProps = (state: RootState) => ({
  isAuth: state.auth.isAuth,
  isParticipanteEmissor: state.auth.usuario.participante.funcao === FuncaoParticipante.Emissor,
  isParticipanteEscriturador: state.auth.usuario.participante.funcao === FuncaoParticipante.Escriturador,
  isUsuarioCadastroLeitura: state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroLeitura,
  isUsuarioControlesInternos: state.auth.usuario.grupoAcesso === GrupoAcesso.ControlesInternos,
  isUsuarioOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes,
  isUsuarioCadastroOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes,
  isUsuarioOperacoesLeitura: state.auth.usuario.grupoAcesso === GrupoAcesso.OperacoesLeitura,
  isUsuarioGestaoAcessos: state.auth.usuario.grupoAcesso === GrupoAcesso.GestaoAcessos,
  isUsuarioComum: state.auth.usuario.grupoAcesso === GrupoAcesso.Comum,
  isParticipanteCustodiante: state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante,
  isUsuarioMaster: state.auth.usuario.participante.perfil === Perfil.Master,
  isParticipanteCredenciado: state.auth.usuario.participante.funcao === FuncaoParticipante.Credenciado,
  isUsuarioLeitura: state.auth.usuario.participante.perfil === Perfil.Leitura,
  modulos: state.auth.usuario.modulos,
  participanteFuncaoUsuarioId: state.auth.usuario.participanteFuncaoUsuarioId,
  isUsuarioModuloInstrumentoFinanceiro: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.INSTRUMENTO_FINANCEIRO)),
  isUsuarioModuloAprovacoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.APROVACOES)),
  isUsuarioModuloSolicitacoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.SOLICITACOES)),
  isUsuarioModuloParticipantes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.PARTICIPANTES)),
  isUsuarioModuloInvestidores: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.INVESTIDORES)),
  isUsuarioModuloMovimentacoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.MOVIMENTACOES)),
  isUsuarioModuloGestaoAcessos: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.GESTAO_ACESSOS)),
  isUsuarioModuloParametrizacoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.PARAMETRIZACOES)),
  isUsuarioModuloSolicitacaoCredenciamento: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.SOLICITACAO_CREDENCIAMENTO)),
  isUsuarioModuloCertidoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.CERTIDOES)),
  isUsuarioModuloUploadArquivos: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.UPLOAD_ARQUIVOS)),
  isUsuarioModuloLiquidacoes: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.LIQUIDACOES)),
  isUsuarioModuloDelimitacaoEmLote: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.DELIMITACAO_LOTE)),
  isUsuarioModuloLancamentoPU: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.LANCAMENTO_PU)),
  isUsuarioModuloAtualizacaoCotas: Boolean(state.auth.usuario.modulos.find((modulo) => modulo.nomeEnum === Modulos.ATUALIZACAO_COTAS)),
});

const mapDispatchToProps = { logout, saveModulos };

const Header: React.FC<Props> = (props) => {
  const {
    location: { pathname },
    isParticipanteEmissor,
    isParticipanteEscriturador,
    isParticipanteCustodiante,
    isUsuarioCadastroLeitura,
    isUsuarioControlesInternos,
    isUsuarioOperacoes,
    isUsuarioOperacoesLeitura,
    isUsuarioCadastroOperacoes,
    isUsuarioMaster,
    isParticipanteCredenciado,
    isUsuarioGestaoAcessos,
    isUsuarioComum,
    isUsuarioLeitura,
    isUsuarioModuloInstrumentoFinanceiro,
    isUsuarioModuloAprovacoes,
    isUsuarioModuloParticipantes,
    isUsuarioModuloSolicitacoes,
    isUsuarioModuloInvestidores,
    isUsuarioModuloMovimentacoes,
    isUsuarioModuloGestaoAcessos,
    isUsuarioModuloParametrizacoes,
    isUsuarioModuloSolicitacaoCredenciamento,
    isUsuarioModuloCertidoes,
    isUsuarioModuloUploadArquivos,
    isUsuarioModuloLiquidacoes,
    isUsuarioModuloDelimitacaoEmLote,
    isUsuarioModuloLancamentoPU,
    isUsuarioModuloAtualizacaoCotas,
    modulos,
    saveModulos,
    participanteFuncaoUsuarioId,
  } = props;

  const [value, setValue] = useState<string>('/aprovacoes');
  const [openLogoutDrawer, setOpenLogoutDrawer] = useState<boolean>(false);
  const [tabs, setTabs] = useState<Array<TabsProps>>([]);

  const usuarioApi = useSelector((state: RootState) => state.usuarioApi);
  const dispatch = useDispatch();

  useEffect(() => {
    const hasSameRoute = tabs.some((tab) => tab.value === pathname);
    if (hasSameRoute) setValue(pathname);
    else {
      const partialRoute = tabs.filter((tab) => tab.value !== '/').find((tab) => pathname.includes(tab.value));
      setValue(partialRoute ? partialRoute.value : '/aprovacoes');
    }
  }, [pathname, tabs]);

  const logout = () => {
    setOpenLogoutDrawer(false);
    props.logout();
  };

  useEffect(() => {
    usuarioApi
      .login(dispatch, participanteFuncaoUsuarioId)
      .then((result) => {
        saveModulos({ ...result, participanteFuncaoUsuarioId: participanteFuncaoUsuarioId });
      })
      .catch(() => { });
  }, [dispatch, usuarioApi, value]);

  type TabsProps = LinkProps & TabProps;

  useEffect(() => {
    let tempTabs: Array<TabsProps> = [];
    if (isUsuarioModuloInstrumentoFinanceiro) {
      tempTabs.push({
        to: '/instrumentos/gerenciar',
        icon: <Description />,
        label: 'Instrumentos Financeiros',
        value: '/instrumentos/gerenciar',
      });
    }

    if (isUsuarioModuloDelimitacaoEmLote) {
      tempTabs.push({
        to: '/distribuicao',
        icon: <FolderOpenOutlined />,
        label: 'Distribuição',
        value: '/distribuicao',
        children: [
          {
            to: '/delimitacao-em-lote',
            icon: <FolderOpenOutlined />,
            label: 'Delimitação de Lotes',
            value: '/delimitacao-em-lote',
          },
          {
            to: '/mapa-alocacao',
            icon: <PublicOutlined />,
            label: 'Painel de Alocação',
            value: '/mapa-alocacao',
          },
          {
            to: '/lancamento-pu',
            icon: <MonetizationOnRounded />,
            label: 'Lançamento de PU',
            value: '/lancamento-pu',
          }
        ],
      });
    }

    if (isUsuarioModuloAprovacoes) {
      tempTabs.push({
        to: '/aprovacoes',
        icon: <CheckCircleIcon />,
        label: 'Aprovações',
        value: '/aprovacoes',
      });
    }

    if (isUsuarioModuloParticipantes) {
      tempTabs.push({
        to: '/gerenciar/participantes',
        icon: <Person />,
        label: 'Participantes',
        value: '/gerenciar/participantes',
      });
    }

    if (isUsuarioModuloSolicitacoes) {
      tempTabs = tempTabs.concat([
        {
          to: '/solicitacoes',
          icon: <CheckCircleIcon />,
          label: 'Solicitações',
          value: '/solicitacoes',
        },
      ]);
    }
    if (isUsuarioModuloInvestidores) {
      tempTabs.push({
        to: '/gerenciar/investidores',
        icon: <SvgIcon component={SvgInvestidor} />,
        label: 'Investidores',
        value: '/gerenciar/investidores',
      });
    }

    if (isUsuarioModuloMovimentacoes) {
      tempTabs.push({
        to: '/operacoes/gerenciar',
        icon: <InsertDriveFile />,
        label: 'Movimentações',
        value: '/operacoes/gerenciar',
      });
    }

    if (isUsuarioModuloAtualizacaoCotas) {
      tempTabs.push({
        to: '/cotas-de-fundo/atualizacao-cota',
        icon: <Update />,
        label: 'Atualização de Cotas',
        value: '/cotas-de-fundo/atualizacao-cota',
      });
    }

    if (isUsuarioModuloGestaoAcessos || (isUsuarioComum && isUsuarioMaster)) {
      tempTabs.push({
        to: '/gerenciar/acessos',
        icon: <LockOpen />,
        label: 'Gestão de Acessos',
        value: '/gerenciar/acessos',
      });
    }

    if (isUsuarioModuloSolicitacaoCredenciamento) {
      tempTabs.push({
        to: '/credenciados/emissores',
        icon: <Group />,
        label: 'Solicitação credenciado emissor',
        value: '/credenciados/emissores',
      });
    }

    if (isUsuarioModuloParametrizacoes) {
      tempTabs.push({
        to: '/parametrizacoes',
        icon: <Group />,
        label: 'Parametrizações',
        value: '/parametrizacoes',
      });
    }

    if (isUsuarioModuloCertidoes) {
      tempTabs.push({
        to: '/certidoes',
        icon: <GetAppIcon />,
        label: 'Certidões',
        value: '/certidoes',
      });
    }

    if (isUsuarioModuloUploadArquivos) {
      tempTabs.push({
        to: '/upload_arquivos',
        icon: <CloudUpload />,
        label: 'Integração via arquivos',
        value: '/upload_arquivos',
      });
    }

    if (isUsuarioModuloLiquidacoes) {
      tempTabs.push({
        to: '/liquidacoes',
        icon: <MonetizationOn />,
        label: 'Liquidações',
        value: '/liquidacoes',
      });
    }

    setTabs(tempTabs);
  }, [
    isParticipanteEmissor,
    isParticipanteEscriturador,
    isParticipanteCustodiante,
    isUsuarioControlesInternos,
    isUsuarioOperacoes,
    isUsuarioCadastroLeitura,
    isUsuarioOperacoesLeitura,
    isParticipanteCredenciado,
    modulos,
    value,
  ]);

  const logoutDrawer = (
    <Drawer
      anchor={'right'}
      open={openLogoutDrawer}
      onClose={() => setOpenLogoutDrawer(false)}
      PaperProps={{ style: { padding: '40px', maxWidth: 460 } }}
    >
      <Grid container>
        <Grid item md={12}>
          <Typography variant="h3">{'Logout'}</Typography>
          <SmSpace />
        </Grid>
        <Grid item md={12}>
          <Divider variant="fullWidth" />
          <SmSpace />
        </Grid>
        <Grid item md={12}>
          <Typography variant="body2">{'Tem certeza que deseja sair?'}</Typography>
        </Grid>
      </Grid>
      <SmSpace />
      <SmSpace />
      <Grid>
        <Button onClick={logout} variant="contained" color="primary" fullWidth>
          {'Sair da Depositária'}
        </Button>
        <SmSpace />
        <Button onClick={() => setOpenLogoutDrawer(false)} variant="contained" color="secondary" fullWidth>
          {'Não, me mantenha logado'}
        </Button>
      </Grid>
    </Drawer>
  );

  return (
    <AppBar className='no-padding' position="fixed" color="secondary" style={{ height: '100vh', width: HEADER_WIDTH, left: 0 }} >
      <Toolbar disableGutters>
        <div className='container-header'>
          <div style={{ display: 'flex', flexDirection: 'column', alignContent: 'center' }}>
            <StyledIconButton disableFocusRipple data-testid="btnMenuLogo">
              <img src={laqusImf} alt="Logo Laqus IMF digital" />
            </StyledIconButton>
            <Tabs orientation="vertical" indicatorColor="primary" value={value}>
              {tabs.map((tab, index) => {

                if (tab.children && Array.isArray(tab.children) && tab.children.length > 0) {
                  return <TabWithSubmenu tab={tab} index={index} value={value} key={index} />;
                }

                return <LinkTab component={Link} key={index} {...tab} />;
              })}
            </Tabs>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignContent: 'center' }}>
            <StyledIconButton
              disableFocusRipple
              onClick={() => setOpenLogoutDrawer(true)}
              style={{ marginBottom: 20 }}
              data-testid="btnMenuLogout"
            >
              <ExitToAppIcon />
            </StyledIconButton>
          </div>
        </div>
      </Toolbar>
      {logoutDrawer}
    </AppBar>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Header));